import useCampaignTypesData from './useCampaignTypesData';
import type { DateRange, SelectedNetwork, ShowFilter, VisibleCategories } from '@/types';
import CampaignTypeEarningsChart from '@/components/charts/CampaignTypeEarningsChart';
import Loader from '@/components/Loader';
import GenericError from '@/components/states/GenericError';
import { usePageMetadata } from '@/hooks';

interface CampaignTypesEarningsChartContainerProps {
  selectedNetwork: SelectedNetwork;
  dateRange: DateRange;
  visibleCategories: VisibleCategories;
  modalSelectedShow: ShowFilter;
}

/**
 * This is the container for the campaign type earnings chart.
 * It is used to display the earnings per day for each campaign type.
 *
 * @param selectedNetwork - The selected network.
 * @param dateRange - The date range.
 * @param visibleCategories - The visible categories for the campaign types modal.
 * @returns The campaign type earnings container.
 */
const CampaignTypesEarningsChartContainer = ({
  selectedNetwork,
  dateRange,
  visibleCategories,
  modalSelectedShow,
}: CampaignTypesEarningsChartContainerProps) => {
  const { page: pageType } = usePageMetadata();
  const {
    data: campaignTypesData,
    isPending: isLoading,
    isError,
    error,
  } = useCampaignTypesData(selectedNetwork, dateRange, pageType === 'earnings', modalSelectedShow);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[35dvh]">
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <GenericError
        message={
          error?.message || 'Unable to display campaign type earnings for the selected time period'
        }
      />
    );
  }

  // Extract currency from the first data point that has one
  const currency =
    campaignTypesData?.[0]?.totals &&
    Object.values(campaignTypesData[0].totals).find((cat) => cat.total.currency)?.total.currency;

  return (
    <div data-testid="campaign-types-graph-container">
      <CampaignTypeEarningsChart
        data={campaignTypesData ?? []}
        currency={currency ?? ''}
        visibleCategories={visibleCategories}
      />
    </div>
  );
};

export default CampaignTypesEarningsChartContainer;
